import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * OTPInput
 * Props:
 *  - length (number) default 6
 *  - value (string) optional controlled value
 *  - defaultValue (string) optional uncontrolled initial value
 *  - onChange (fn) called with current value on every change
 *  - onComplete (fn) called when value length === length
 *  - autoFocus (bool)
 *  - disabled (bool)
 *  - inputClassName (string)
 */
export default function OTPInput({
    length = 6,
    value,
    defaultValue = "",
    onChange,
    onComplete,
    autoFocus = false,
    disabled = false,
    inputClassName = "",
}) {
    const isControlled = typeof value === "string";
    const [internal, setInternal] = useState(() =>
        (defaultValue || "").slice(0, length).padEnd(length, "")
    );

    // array of chars (strings). Use controlled value if provided.
    const chars = (isControlled ? value : internal) || "";
    const refInputs = useRef([]);

    useEffect(() => {
        if (autoFocus && !disabled) {
            // focus first empty input or first input
            const firstEmpty = chars.split("").findIndex((c) => !c);
            const idx = firstEmpty === -1 ? 0 : firstEmpty;
            refInputs.current[idx]?.focus?.();
        }
    }, [autoFocus, disabled]); // run on mount if autoFocus

    // helper to set value (internal or via onChange)
    const setValue = (next) => {
        const v = (next || "").slice(0, length);
        if (!isControlled) setInternal(v);
        onChange?.(v);
        if (v.length === length) onComplete?.(v);
    };

    // handle input change for a specific index
    const handleChange = (e, idx) => {
        if (disabled) return;
        const raw = e.target.value || "";
        // accept only digits/word characters; keep first char
        const char = raw.replace(/\s/g, "")[0] || "";
        if (!char) {
            // user cleared this box
            const arr = chars.split("");
            arr[idx] = "";
            setValue(arr.join(""));
            return;
        }

        // If user typed more than one char (mobile keyboards), spread into subsequent inputs
        const remaining = raw.replace(/\s/g, "");
        const arr = chars.split("").slice(0, length);
        for (let i = 0; i < remaining.length && idx + i < length; i++) {
            arr[idx + i] = remaining[i];
        }
        const newVal = arr.join("");
        setValue(newVal);

        // focus next empty
        const nextIndex = Math.min(length - 1, idx + remaining.length);
        const firstEmpty = arr.findIndex((c, i) => !c && i >= idx);
        const focusIdx = firstEmpty === -1 ? nextIndex : firstEmpty;
        setTimeout(() => refInputs.current[focusIdx]?.focus?.(), 0);
    };

    const handleKeyDown = (e, idx) => {
        if (disabled) return;
        const key = e.key;

        if (key === "Backspace") {
            // if current has value clear it, else move back and clear
            if (!chars[idx]) {
                const prev = Math.max(0, idx - 1);
                refInputs.current[prev]?.focus?.();
                const arr = chars.split("");
                arr[prev] = "";
                setValue(arr.join(""));
                e.preventDefault();
            } else {
                const arr = chars.split("");
                arr[idx] = "";
                setValue(arr.join(""));
                // keep focus here
                e.preventDefault();
            }
        } else if (key === "ArrowLeft") {
            refInputs.current[Math.max(0, idx - 1)]?.focus?.();
            e.preventDefault();
        } else if (key === "ArrowRight") {
            refInputs.current[Math.min(length - 1, idx + 1)]?.focus?.();
            e.preventDefault();
        } else if (key === "Enter") {
            // no-op, allow form submit
        } else {
            // allow normal input keys
        }
    };

    const handlePaste = (e) => {
        if (disabled) return;
        e.preventDefault();
        const pasted = (e.clipboardData || window.clipboardData).getData("Text") || "";
        const digits = pasted.replace(/\s/g, "");
        if (!digits) return;
        const arr = chars.split("").slice(0, length);
        for (let i = 0; i < digits.length && i < length; i++) {
            arr[i] = digits[i];
        }
        const newVal = arr.join("");
        setValue(newVal);
        // focus first empty or last
        const firstEmpty = arr.findIndex((c) => !c);
        const focusIdx = firstEmpty === -1 ? Math.min(digits.length, length - 1) : firstEmpty;
        setTimeout(() => refInputs.current[focusIdx]?.focus?.(), 0);
    };

    // build inputs
    const inputs = [];
    for (let i = 0; i < length; i++) {
        inputs.push(
            <input
                key={i}
                ref={(el) => (refInputs.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d*"
                maxLength={length} // allow multi-fill but we'll slice
                value={chars[i] || ""}
                onChange={(e) => handleChange(e, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                disabled={disabled}
                aria-label={`OTP digit ${i + 1}`}
                className={inputClassName || "otp-input"}
                style={{
                    width: 44,
                    height: 44,
                    textAlign: "center",
                    fontSize: 18,
                    marginRight: 8,
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    outline: "none",
                }}
            />
        );
    }

    return (
        <div
            onPaste={handlePaste}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
            role="group"
            aria-label={`OTP input ${length} digits`}
        >
            {inputs}
        </div>
    );
}

OTPInput.propTypes = {
    length: PropTypes.number,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    onChange: PropTypes.func,
    onComplete: PropTypes.func,
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    inputClassName: PropTypes.string,
};